import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useBreadcrumb, BreadcrumbItem } from "@/contexts/BreadcrumbContext";
import { MapPicker } from "@/components/MapPicker";

interface Event {
  _id: string;
  name: string;
  location?: string;
  lat?: number;
  lng?: number;
  description?: string;
  price: number;
  start?: string;
  end?: string;
  timezone?: string;
  url?: string;
  capacity: number;
  tourId?: string;
}

export function EventEdit() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<Event | null>(null);
  const [tourId, setTourId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  const [formData, setFormData] = useState({
    name: "",
    location: "",
    lat: "",
    lng: "",
    description: "",
    price: "",
    start: "",
    end: "",
    timezone: "",
    url: "",
    capacity: "",
  });

  useEffect(() => {
    async function fetchEvent() {
      if (!eventId) return;

      try {
        // Fetch event
        const response = await fetch(`/api/events/${eventId}`);
        if (response.ok) {
          const data = await response.json();
          const eventData = data.event;
          setEvent(eventData);
          
          // Store tourId if it exists
          if (eventData.tourId) {
            setTourId(eventData.tourId);
          }

          // Fetch tour to get its name for breadcrumb if tourId exists
          let tourName = "Tour";
          if (eventData.tourId) {
            const tourResponse = await fetch(`/api/tours/${eventData.tourId}`);
            if (tourResponse.ok) {
              const tourData = await tourResponse.json();
              if (tourData.tour?.name) {
                tourName = tourData.tour.name;
              }
            }
          }
          
          // Format dates for input fields (YYYY-MM-DDTHH:mm)
          const formatDate = (dateString?: string) => {
            if (!dateString) return "";
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return "";
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            const hours = String(date.getHours()).padStart(2, "0");
            const minutes = String(date.getMinutes()).padStart(2, "0");
            return `${year}-${month}-${day}T${hours}:${minutes}`;
          };

          setFormData({
            name: eventData.name || "",
            location: eventData.location || "",
            lat: eventData.lat?.toString() || "",
            lng: eventData.lng?.toString() || "",
            description: eventData.description || "",
            price: eventData.price?.toString() || "",
            start: formatDate(eventData.start),
            end: formatDate(eventData.end),
            timezone: eventData.timezone || "",
            url: eventData.url || "",
            capacity: eventData.capacity?.toString() || "",
          });

          // Set breadcrumbs with tour and event names
          const eventName = eventData?.name || "Event";
          const breadcrumbs: BreadcrumbItem[] = [];
          
          if (eventData.tourId) {
            breadcrumbs.push(
              { label: "Tours", path: "/tours" },
              { label: tourName, path: `/tours/${eventData.tourId}` },
              { label: eventName, path: `/events/${eventId}/edit` }
            );
          } else {
            breadcrumbs.push(
              { label: "Events", path: "/events" },
              { label: eventName, path: `/events/${eventId}/edit` }
            );
          }
          
          setBreadcrumbs(breadcrumbs);
        } else {
          console.error("Failed to fetch event");
        }
      } catch (error) {
        console.error("Failed to fetch event:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchEvent();

    return () => {
      clearBreadcrumbs();
    };
  }, [eventId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLocationSelect = (lat: number, lng: number) => {
    setFormData((prev) => ({
      ...prev,
      lat: lat.toString(),
      lng: lng.toString(),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!eventId) return;

    setIsSubmitting(true);
    try {
      const payload: any = {
        name: formData.name,
        location: formData.location || undefined,
        description: formData.description || undefined,
        url: formData.url || undefined,
        timezone: formData.timezone || undefined,
        price: formData.price ? parseFloat(formData.price) : 0,
        capacity: formData.capacity ? parseInt(formData.capacity, 10) : 0,
      };

      if (formData.lat) {
        payload.lat = parseFloat(formData.lat);
      }
      if (formData.lng) {
        payload.lng = parseFloat(formData.lng);
      }
      if (formData.start) {
        payload.start = new Date(formData.start).toISOString();
      }
      if (formData.end) {
        payload.end = new Date(formData.end).toISOString();
      }

      const response = await fetch(`/api/events/${eventId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        // Navigate back to tour if it exists, otherwise to events list
        if (tourId) {
          navigate(`/tours/${tourId}`);
        } else {
          navigate(`/events`);
        }
      } else {
        const data = await response.json();
        console.error("Failed to update event:", data.error);
        alert(data.error || "Failed to update event");
      }
    } catch (error) {
      console.error("Failed to update event:", error);
      alert("An error occurred while updating the event");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <div className="p-4">Loading...</div>;
  }

  if (!event) {
    return <div className="p-4">Event not found</div>;
  }

  return (
    <div className="p-4">
      <Card>
        <CardHeader>
          <CardTitle>Edit Event</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Textarea
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                rows={4}
              />
            </div>

            <div className="space-y-2">
              <Label>Location on Map</Label>
              <MapPicker
                lat={formData.lat ? (isNaN(parseFloat(formData.lat)) ? undefined : parseFloat(formData.lat)) : undefined}
                lng={formData.lng ? (isNaN(parseFloat(formData.lng)) ? undefined : parseFloat(formData.lng)) : undefined}
                onLocationSelect={handleLocationSelect}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="lat">Latitude</Label>
                <Input
                  id="lat"
                  name="lat"
                  type="number"
                  step="any"
                  value={formData.lat}
                  onChange={handleChange}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lng">Longitude</Label>
                <Input
                  id="lng"
                  name="lng"
                  type="number"
                  step="any"
                  value={formData.lng}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="price">Price *</Label>
                <Input
                  id="price"
                  name="price"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="capacity">Capacity *</Label>
                <Input
                  id="capacity"
                  name="capacity"
                  type="number"
                  value={formData.capacity}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start">Start Date/Time</Label>
                <Input
                  id="start"
                  name="start"
                  type="datetime-local"
                  value={formData.start}
                  onChange={handleChange}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end">End Date/Time</Label>
                <Input
                  id="end"
                  name="end"
                  type="datetime-local"
                  value={formData.end}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="timezone">Timezone</Label>
              <Input
                id="timezone"
                name="timezone"
                value={formData.timezone}
                onChange={handleChange}
                placeholder="e.g., America/New_York"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="url">URL</Label>
              <Input
                id="url"
                name="url"
                type="url"
                value={formData.url}
                onChange={handleChange}
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (tourId) {
                    navigate(`/tours/${tourId}`);
                  } else {
                    navigate(`/events`);
                  }
                }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : "Save"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

